import { supabase } from "@/integrations/supabase/client";
import { TimeRangeType } from "./useReportsData";

// Types for data structures
interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface ReportDataResult {
  success: boolean;
  categories?: any[];
  tailors?: any[];
  subscriptions?: any[];
  orders?: any[];
  error?: Error;
}

// Helper function to calculate date range based on time range
export const getDateRange = (timeRange: TimeRangeType) => {
  const endDate = new Date();
  const startDate = new Date();
  
  switch (timeRange) {
    case 'week':
      startDate.setDate(endDate.getDate() - 7);
      break;
    case 'month':
      startDate.setMonth(endDate.getMonth() - 1);
      break;
    case 'quarter':
      startDate.setMonth(endDate.getMonth() - 3);
      break;
    case 'year':
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
  }
  
  return { startDate, endDate };
};

// Fetch all report data from the database
export const fetchReportData = async (dateRange: DateRange, timeRange: TimeRangeType): Promise<ReportDataResult> => {
  try {
    console.log("Fetching real database data for reports...");
    
    // Fetch main data tables concurrently  
    const [ordersResult, categoriesResult, tailorsResult, subscriptionsResult] = await Promise.all([
      // Orders query with time filtering and limited fields
      supabase
        .from("orders")
        .select(`
          id,
          status,
          created_at,
          due_date,
          total_amount,
          customer_id,
          factory_id,
          tailor_id,
          product_id
        `)
        .gte('created_at', dateRange.startDate.toISOString())
        .order("created_at", { ascending: false })
        .limit(100),
      
      // Categories (products)
      supabase
        .from("products")
        .select("*"),
      
      // Tailors
      supabase
        .from("users")
        .select("*")
        .eq("role", "tailor"),
      
      // Subscriptions
      supabase
        .from("subscriptions")
        .select("*"),
    ]);
    
    // Check for errors
    if (ordersResult.error) throw ordersResult.error;
    if (categoriesResult.error) throw categoriesResult.error;
    if (tailorsResult.error) throw tailorsResult.error;
    if (subscriptionsResult.error) throw subscriptionsResult.error;
    
    const orders = ordersResult.data || [];
    const categories = categoriesResult.data || [];
    const tailors = tailorsResult.data || [];
    const subscriptions = subscriptionsResult.data || [];
    
    console.log("Fetched data:", {
      orders: orders.length,
      categories: categories.length,
      tailors: tailors.length,
      subscriptions: subscriptions.length
    });
    
    return {
      success: true,
      orders,
      categories,
      tailors,
      subscriptions
    };
  } catch (error: any) {
    console.error("Error in fetchReportData:", error);
    return {
      success: false,
      error
    };
  }
};

// Helper function to validate and clean order data
export const validateOrderData = (orders: any[]): any[] => {
  return orders.filter(order => {
    // Basic validation to ensure order has required fields
    return order && 
           order.id && 
           order.created_at && 
           typeof order.total_amount !== 'undefined';
  }).map(order => ({
    ...order,
    // Ensure total_amount is a valid number
    total_amount: parseFloat(order.total_amount) || 0,
    // Ensure status is valid
    status: order.status || 'saved'
  }));
};

// Filter data based on selected time range
export const filterByTimeRange = (data: any[], dateRange: DateRange) => {
  if (!data.length) return data;
  
  const { startDate } = dateRange;
  return data.filter(item => {
    const itemDate = new Date(item.created_at);
    return itemDate >= startDate;
  });
};